import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "../lib/api";
import {
  Plus, Trash2, X, Upload, Download, FileText, GraduationCap, Users, Calendar,
  MapPin, Video, Award, MessageSquare, Send, UserPlus, CheckCircle2, XCircle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { DatePicker } from "@/components/ui/date-picker";
import { formatTanggal, hariIniISO } from "@/lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";

// KENAPA daftar ini di frontend saja (bukan endpoint backend): kategori
// pelatihan teks bebas seperti "brand" di modul lain (lihat CLAUDE.md) —
// backend menerima string apa pun, daftar ini cuma kemudahan pengetikan di
// dropdown. HR tetap bisa mengetik kategori baru lewat opsi "Lainnya".
const KATEGORI_PELATIHAN = [
  "Onboarding", "Produk & Teknis", "Soft Skill & Layanan Pelanggan",
  "Kepatuhan & SOP", "Sertifikasi Vendor/Brand", "Kepemimpinan", "Lainnya",
];

const MODE_LABEL = { offline: "Tatap Muka", online: "Daring", hybrid: "Hybrid" };
const MODE_ICON = { offline: MapPin, online: Video, hybrid: Users };

const STATUS_TRAINING = [
  { v: "terjadwal", label: "Terjadwal", tone: "bg-sky-100 text-sky-800" },
  { v: "berlangsung", label: "Berlangsung", tone: "bg-amber-100 text-amber-800" },
  { v: "selesai", label: "Selesai", tone: "bg-emerald-100 text-emerald-800" },
  { v: "dibatalkan", label: "Dibatalkan", tone: "bg-rose-100 text-rose-800" },
];

const STATUS_PESERTA = [
  { v: "terdaftar", label: "Terdaftar", tone: "bg-slate-100 text-slate-700" },
  { v: "hadir", label: "Hadir", tone: "bg-emerald-100 text-emerald-800" },
  { v: "tidak_hadir", label: "Tidak Hadir", tone: "bg-rose-100 text-rose-800" },
  { v: "batal", label: "Batal", tone: "bg-slate-200 text-slate-500" },
];

const emptyForm = {
  judul: "", kategori: "Onboarding", mode: "offline",
  tanggal_mulai: hariIniISO(), tanggal_selesai: "", jam_mulai: "", jam_selesai: "",
  lokasi: "", pengajar: "", penyelenggara: "internal", kuota: "", deskripsi: "",
  branch: "", status: "terjadwal",
};

const emptySertifikat = { nomor: "", tanggal_terbit: "", berlaku_sampai: "", catatan: "" };

export default function Pelatihan() {
  const { user, can } = useAuth();
  // Sama seperti backend `_batas_akses_pelatihan()`: superadmin/admin/pemegang
  // izin "pelatihan" melihat & mengelola semua jadwal; selain itu (karyawan
  // biasa) otomatis hanya melihat riwayat miliknya sendiri — TANPA kunci izin
  // ini. Jangan gerbangi seluruh halaman dengan `can()`, nanti karyawan biasa
  // ikut ditolak melihat riwayatnya sendiri.
  const isAdmin = user?.role === "superadmin" || user?.role === "admin" || can("pelatihan", "view");
  const canCreate = can("pelatihan", "create");
  const canUpdate = can("pelatihan", "update");
  const canDelete = can("pelatihan", "delete");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emps, setEmps] = useState([]);
  const [filter, setFilter] = useState({ status: "", kategori: "", q: "" });
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detailFor, setDetailFor] = useState(null);
  const [pesertaList, setPesertaList] = useState([]);
  const [addPesertaModal, setAddPesertaModal] = useState(false);
  const [pickedEmpIds, setPickedEmpIds] = useState([]);
  const [editPesertaFor, setEditPesertaFor] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filter.status) p.set("status", filter.status);
    if (filter.kategori) p.set("kategori", filter.kategori);
    if (filter.q) p.set("q", filter.q);
    api.get(`/pelatihan?${p}`).then(r => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, [filter.status, filter.kategori, filter.q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isAdmin) api.get("/employees").then(r => setEmps(r.data.filter(e => e.is_active !== false))).catch(() => {});
  }, [isAdmin]);

  const openNew = () => { setForm(emptyForm); setEditing(null); setModal(true); };
  const openEdit = (t) => {
    setForm({
      judul: t.judul, kategori: t.kategori || "Onboarding", mode: t.mode || "offline",
      tanggal_mulai: t.tanggal_mulai || hariIniISO(), tanggal_selesai: t.tanggal_selesai || "",
      jam_mulai: t.jam_mulai || "", jam_selesai: t.jam_selesai || "", lokasi: t.lokasi || "",
      pengajar: t.pengajar || "", penyelenggara: t.penyelenggara || "internal",
      kuota: t.kuota ?? "", deskripsi: t.deskripsi || "", branch: t.branch || "",
      status: t.status || "terjadwal",
    });
    setEditing(t.id); setModal(true);
  };

  const save = async () => {
    if (!form.judul.trim()) { toast.error("Judul wajib diisi"); return; }
    if (!form.tanggal_mulai) { toast.error("Tanggal mulai wajib diisi"); return; }
    const payload = { ...form, kuota: form.kuota === "" ? null : Number(form.kuota) };
    try {
      if (editing) {
        await api.patch(`/pelatihan/${editing}`, payload);
        toast.success("Pelatihan diperbarui");
      } else {
        await api.post("/pelatihan", payload);
        toast.success("Pelatihan dibuat");
      }
      setModal(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  // Form 12 field (judul, jadwal, lokasi, kuota, dst) — JSX modal ada di
  // komponen halaman yang tidak unmount, jadi `active` mengikuti `modal`.
  const pelatihanGuard = useModalGuard({
    active: modal, dangerous: true,
    onClose: () => setModal(false), onSubmit: save,
  });

  const del = async (id) => {
    if (!confirm("Hapus pelatihan ini? Seluruh data peserta & hasilnya ikut terhapus.")) return;
    try {
      await api.delete(`/pelatihan/${id}`);
      toast.success("Pelatihan dihapus"); setDetailFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  const openDetail = async (t) => {
    try {
      const { data } = await api.get(`/pelatihan/${t.id}`);
      setDetailFor(data);
      if (isAdmin) {
        const pr = await api.get(`/pelatihan/${t.id}/peserta`);
        setPesertaList(pr.data);
      } else {
        setPesertaList(data.peserta_saya ? [data.peserta_saya] : []);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuka detail"); }
  };

  const refreshDetail = async () => {
    if (!detailFor) return;
    const { data } = await api.get(`/pelatihan/${detailFor.id}`);
    setDetailFor(data);
    if (isAdmin) {
      const pr = await api.get(`/pelatihan/${detailFor.id}/peserta`);
      setPesertaList(pr.data);
    }
    load();
  };

  // ---- Peserta ----
  const submitAddPeserta = async () => {
    if (pickedEmpIds.length === 0) { toast.error("Pilih minimal 1 karyawan"); return; }
    try {
      const { data } = await api.post(`/pelatihan/${detailFor.id}/peserta`, { employee_ids: pickedEmpIds });
      toast.success(`${data.n_ditambahkan} peserta ditambahkan${data.n_dilewati ? `, ${data.n_dilewati} dilewati (sudah terdaftar)` : ""}`);
      setAddPesertaModal(false); setPickedEmpIds([]);
      await refreshDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah peserta"); }
  };

  const delPeserta = async (pid) => {
    if (!confirm("Hapus peserta ini dari pelatihan?")) return;
    try {
      await api.delete(`/pelatihan/${detailFor.id}/peserta/${pid}`);
      toast.success("Peserta dihapus"); await refreshDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus peserta"); }
  };

  const savePeserta = async (pid, patch) => {
    try {
      await api.patch(`/pelatihan/${detailFor.id}/peserta/${pid}`, patch);
      toast.success("Data peserta diperbarui"); setEditPesertaFor(null); await refreshDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  // ---- Materi ----
  const readAsBase64 = (f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1] || "");
    r.onerror = rej;
    r.readAsDataURL(f);
  });

  const onPickMateri = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: > 10MB (dilewati)`); continue; }
      try {
        const base64 = await readAsBase64(f);
        await api.post(`/pelatihan/${detailFor.id}/materi`, {
          name: f.name, mime: f.type || "application/octet-stream", size: f.size, base64,
        });
      } catch (err) { toast.error(`${f.name}: gagal diunggah`); }
    }
    toast.success("Materi diunggah");
    await refreshDetail();
  };

  const downloadMateri = async (tid, fname) => {
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/pelatihan/${tid}/materi/${encodeURIComponent(fname)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Gagal mengunduh"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = fname;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const delMateri = async (fname) => {
    if (!confirm(`Hapus materi ${fname}?`)) return;
    await api.delete(`/pelatihan/${detailFor.id}/materi/${encodeURIComponent(fname)}`);
    await refreshDetail();
  };

  // ---- Komentar ----
  const addComment = async (text) => {
    if (!text.trim()) return false;
    try {
      await api.post(`/pelatihan/${detailFor.id}/komentar`, { text: text.trim() });
      await refreshDetail();
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); return false; }
  };

  const deleteComment = async (cid) => {
    if (!confirm("Hapus komentar?")) return;
    try {
      await api.delete(`/pelatihan/${detailFor.id}/komentar/${cid}`);
      await refreshDetail();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const availableEmps = useMemo(() => {
    const already = new Set(pesertaList.map(p => p.employee_id));
    return emps.filter(e => !already.has(e.id));
  }, [emps, pesertaList]);

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-start justify-between mb-6 md:mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Pelatihan Karyawan</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">
            {isAdmin ? "Jadwal & Riwayat Pelatihan" : "Riwayat Pelatihan Saya"}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {isAdmin
              ? "Kelola jadwal pelatihan, peserta, kehadiran, hasil, dan sertifikat."
              : "Pelatihan yang pernah Anda ikuti — materi, hasil, dan sertifikat."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={filter.status} onChange={e => setFilter(s => ({ ...s, status: e.target.value }))}
            data-testid="filter-status"
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="">Semua status</option>
            {STATUS_TRAINING.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
          {isAdmin && (
            <select value={filter.kategori} onChange={e => setFilter(s => ({ ...s, kategori: e.target.value }))}
              data-testid="filter-kategori"
              className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="">Semua kategori</option>
              {KATEGORI_PELATIHAN.map(k => <option key={k}>{k}</option>)}
            </select>
          )}
          <input value={filter.q} onChange={e => setFilter(s => ({ ...s, q: e.target.value }))}
            placeholder="Cari judul..." data-testid="filter-search"
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm w-40" />
          {canCreate && (
            <button onClick={openNew} data-testid="btn-add-pelatihan"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">
              <Plus className="w-4 h-4" /> Buat Pelatihan
            </button>
          )}
        </div>
      </header>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="font-display font-semibold text-slate-900">Belum ada pelatihan</p>
          <p className="text-sm text-slate-500 mt-1">
            {canCreate ? 'Klik "Buat Pelatihan" untuk mulai.' : "Belum ada pelatihan yang Anda ikuti."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnimatePresence>
            {items.map(t => {
              const st = STATUS_TRAINING.find(x => x.v === t.status) || STATUS_TRAINING[0];
              const ModeIcon = MODE_ICON[t.mode] || MapPin;
              const ps = t.peserta_saya;
              return (
                <motion.div key={t.id}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  data-testid={`pelatihan-${t.id}`}
                  className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer flex flex-col"
                  onClick={() => openDetail(t)}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-display font-semibold text-slate-900 truncate">{t.judul}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap font-semibold ${st.tone}`}>{st.label}</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">{t.kategori}</p>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatTanggal(t.tanggal_mulai)}</span>
                    <span className="flex items-center gap-1"><ModeIcon className="w-3.5 h-3.5" /> {MODE_LABEL[t.mode] || t.mode}</span>
                  </div>
                  {t.lokasi && <p className="text-xs text-slate-400 truncate mb-3">{t.lokasi}</p>}

                  {ps ? (
                    <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                      <span className={`px-2 py-0.5 rounded-full font-medium ${(STATUS_PESERTA.find(x => x.v === ps.status) || STATUS_PESERTA[0]).tone}`}>
                        {(STATUS_PESERTA.find(x => x.v === ps.status) || STATUS_PESERTA[0]).label}
                      </span>
                      {ps.sertifikat?.berlaku_sampai && (
                        <span className={`flex items-center gap-1 ${ps.sertifikat_kedaluwarsa ? "text-rose-600" : "text-slate-500"}`}>
                          <Award className="w-3.5 h-3.5" /> {ps.sertifikat_kedaluwarsa ? "Kedaluwarsa" : `s.d. ${formatTanggal(ps.sertifikat.berlaku_sampai)}`}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {t.pengajar || "-"}</span>
                      {t.n_komentar > 0 && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {t.n_komentar}</span>}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}

      {/* ---- Create/Edit Modal ---- */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={pelatihanGuard.requestClose}>
          <div ref={pelatihanGuard.contentRef} className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900">{editing ? "Edit Pelatihan" : "Buat Pelatihan Baru"}</h3>
              <button onClick={pelatihanGuard.requestClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Judul Pelatihan *</span>
                <input value={form.judul} onChange={e => setForm(s => ({ ...s, judul: e.target.value }))}
                  data-testid="pelatihan-judul"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Kategori</span>
                  <select value={form.kategori} onChange={e => setForm(s => ({ ...s, kategori: e.target.value }))}
                    data-testid="pelatihan-kategori"
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    {KATEGORI_PELATIHAN.map(k => <option key={k}>{k}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Mode</span>
                  <select value={form.mode} onChange={e => setForm(s => ({ ...s, mode: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    {Object.entries(MODE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Status</span>
                  <select value={form.status} onChange={e => setForm(s => ({ ...s, status: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    {STATUS_TRAINING.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Tanggal Mulai *</span>
                  <DatePicker value={form.tanggal_mulai} onChange={v => setForm(s => ({ ...s, tanggal_mulai: v }))} className="mt-1" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Tanggal Selesai</span>
                  <DatePicker value={form.tanggal_selesai} onChange={v => setForm(s => ({ ...s, tanggal_selesai: v }))} className="mt-1" />
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Lokasi / Link Meeting</span>
                  <input value={form.lokasi} onChange={e => setForm(s => ({ ...s, lokasi: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Pengajar / Trainer</span>
                  <input value={form.pengajar} onChange={e => setForm(s => ({ ...s, pengajar: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Penyelenggara</span>
                  <select value={form.penyelenggara} onChange={e => setForm(s => ({ ...s, penyelenggara: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="internal">Internal</option>
                    <option value="eksternal">Eksternal</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Kuota (kosongkan = tanpa batas)</span>
                  <input type="number" min={1} value={form.kuota} onChange={e => setForm(s => ({ ...s, kuota: e.target.value }))}
                    data-testid="pelatihan-kuota"
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Cabang (kosongkan = semua)</span>
                  <input value={form.branch} onChange={e => setForm(s => ({ ...s, branch: e.target.value }))}
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Deskripsi</span>
                <textarea rows={3} value={form.deskripsi} onChange={e => setForm(s => ({ ...s, deskripsi: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </label>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={pelatihanGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={save} data-testid="btn-save-pelatihan"
                  className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">
                  {editing ? "Perbarui" : "Simpan"}
                </button>
              </div>
              {pelatihanGuard.confirming && (
                <UnsavedGuardBar onStay={pelatihanGuard.cancelClose} onDiscard={pelatihanGuard.confirmDiscard} testid="pelatihan-modal-guard-bar" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---- Detail Modal ---- */}
      {detailFor && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setDetailFor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900 truncate">{detailFor.judul}</h3>
              <button onClick={() => setDetailFor(null)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
              {detailFor.deskripsi && <p className="text-sm text-slate-700 whitespace-pre-wrap">{detailFor.deskripsi}</p>}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div><p className="text-xs text-slate-500">Kategori</p><p className="font-medium">{detailFor.kategori}</p></div>
                <div><p className="text-xs text-slate-500">Tanggal</p><p className="font-medium">{formatTanggal(detailFor.tanggal_mulai)}{detailFor.tanggal_selesai !== detailFor.tanggal_mulai ? ` - ${formatTanggal(detailFor.tanggal_selesai)}` : ""}</p></div>
                <div><p className="text-xs text-slate-500">Mode</p><p className="font-medium">{MODE_LABEL[detailFor.mode] || detailFor.mode}</p></div>
                <div><p className="text-xs text-slate-500">Lokasi</p><p className="font-medium truncate">{detailFor.lokasi || "-"}</p></div>
                <div><p className="text-xs text-slate-500">Pengajar</p><p className="font-medium">{detailFor.pengajar || "-"}</p></div>
                {isAdmin && <div><p className="text-xs text-slate-500">Kuota</p><p className="font-medium">{detailFor.kuota ?? "Tanpa batas"} {isAdmin && detailFor.n_peserta != null ? `(${detailFor.n_peserta} terdaftar)` : ""}</p></div>}
              </div>

              {/* ---- Peserta saya (mode karyawan) ---- */}
              {!isAdmin && detailFor.peserta_saya && (
                <PesertaSayaCard peserta={detailFor.peserta_saya} />
              )}

              {/* ---- Roster peserta (mode admin) ---- */}
              {isAdmin && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-slate-500 font-medium">Peserta ({pesertaList.length})</p>
                    {canCreate && (
                      <button onClick={() => setAddPesertaModal(true)} data-testid="btn-add-peserta"
                        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
                        <UserPlus className="w-3.5 h-3.5" /> Tambah Peserta
                      </button>
                    )}
                  </div>
                  {pesertaList.length === 0 ? (
                    <p className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-lg">Belum ada peserta.</p>
                  ) : (
                    <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 text-sm">
                      {pesertaList.map(p => {
                        const st = STATUS_PESERTA.find(x => x.v === p.status) || STATUS_PESERTA[0];
                        return (
                          <li key={p.id} data-testid={`peserta-row-${p.id}`} className="flex items-center gap-2 px-3 py-2">
                            <div className="flex-1 min-w-0">
                              <p className="truncate font-medium">{p.employee_nama}</p>
                              <p className="text-[10px] text-slate-400">{p.employee_nik} · {p.branch}</p>
                            </div>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.tone}`}>{st.label}</span>
                            {p.nilai != null && <span className="text-xs text-slate-500 w-10 text-right">{p.nilai}</span>}
                            {p.sertifikat?.berlaku_sampai && (
                              <span className={`text-[10px] flex items-center gap-1 ${p.sertifikat_kedaluwarsa ? "text-rose-600" : "text-slate-400"}`}>
                                <Award className="w-3 h-3" /> {formatTanggal(p.sertifikat.berlaku_sampai)}
                              </span>
                            )}
                            {canUpdate && (
                              <button onClick={() => setEditPesertaFor(p)} data-testid={`btn-edit-peserta-${p.id}`}
                                className="p-1.5 hover:bg-slate-100 rounded text-slate-500 text-[10px] font-medium">Edit</button>
                            )}
                            {canDelete && (
                              <button onClick={() => delPeserta(p.id)} data-testid={`btn-del-peserta-${p.id}`}
                                className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}

              {/* ---- Materi ---- */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-500 font-medium">Materi ({detailFor.materi?.length || 0})</p>
                  {canUpdate && (
                    <>
                      <button onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
                        <Upload className="w-3.5 h-3.5" /> Unggah
                      </button>
                      <input ref={fileInputRef} type="file" multiple onChange={onPickMateri}
                        data-testid="pelatihan-materi-input" className="hidden" />
                    </>
                  )}
                </div>
                {(!detailFor.materi || detailFor.materi.length === 0) ? (
                  <p className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-lg">Belum ada materi.</p>
                ) : (
                  <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 text-sm">
                    {detailFor.materi.map(f => (
                      <li key={f.id || f.name} className="flex items-center gap-2 px-3 py-2">
                        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="truncate">{f.name}</p>
                          <p className="text-[10px] text-slate-400">{(f.size / 1024).toFixed(1)} KB</p>
                        </div>
                        <button onClick={() => downloadMateri(detailFor.id, f.name)}
                          className="p-1.5 hover:bg-slate-100 rounded text-slate-500">
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        {canUpdate && (
                          <button onClick={() => delMateri(f.name)}
                            className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* ---- Diskusi ---- */}
              <CommentsThread detailFor={detailFor} userEmail={user?.email} canDeleteAny={canDelete}
                onAdd={addComment} onDelete={deleteComment} />

              {(canUpdate || canDelete) && (
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                  {canDelete && (
                    <button onClick={() => del(detailFor.id)}
                      className="h-10 px-4 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-sm inline-flex items-center gap-1">
                      <Trash2 className="w-4 h-4" /> Hapus
                    </button>
                  )}
                  {canUpdate && (
                    <button onClick={() => { const t = detailFor; setDetailFor(null); openEdit(t); }}
                      className="h-10 px-5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
                      Edit Pelatihan
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ---- Add Peserta Modal ---- */}
      {addPesertaModal && (
        <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setAddPesertaModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h3 className="font-display font-semibold text-slate-900">Tambah Peserta</h3>
              <button onClick={() => setAddPesertaModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6">
              {detailFor?.kuota != null && (
                <p className="text-xs text-slate-500 mb-2">Kuota: {pesertaList.length}/{detailFor.kuota}</p>
              )}
              <div className="border border-slate-200 rounded-lg p-3 max-h-64 overflow-y-auto space-y-1">
                {availableEmps.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">Semua karyawan aktif sudah terdaftar.</p>
                ) : availableEmps.map(e => (
                  <label key={e.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:bg-slate-50 rounded px-1">
                    <input type="checkbox" checked={pickedEmpIds.includes(e.id)}
                      onChange={() => setPickedEmpIds(s => s.includes(e.id) ? s.filter(x => x !== e.id) : [...s, e.id])}
                      data-testid={`pelatihan-assign-${e.id}`} />
                    <span className="truncate">{e.nama} <span className="text-slate-400 text-[10px]">{e.branch}</span></span>
                  </label>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button onClick={() => setAddPesertaModal(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={submitAddPeserta} data-testid="btn-submit-add-peserta"
                  className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">
                  Tambahkan ({pickedEmpIds.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- Edit Peserta Modal (kehadiran / hasil / sertifikat) ---- */}
      {editPesertaFor && (
        <PesertaEditModal peserta={editPesertaFor} onClose={() => setEditPesertaFor(null)}
          onSave={(patch) => savePeserta(editPesertaFor.id, patch)} />
      )}
    </div>
  );
}

function PesertaSayaCard({ peserta }) {
  const st = STATUS_PESERTA.find(x => x.v === peserta.status) || STATUS_PESERTA[0];
  const sert = peserta.sertifikat || {};
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Status Keikutsertaan Saya</p>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.tone}`}>{st.label}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><p className="text-xs text-slate-500">Nilai</p><p className="font-medium">{peserta.nilai ?? "-"}</p></div>
        <div>
          <p className="text-xs text-slate-500">Kelulusan</p>
          <p className="font-medium flex items-center gap-1">
            {peserta.lulus === true && <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Lulus</>}
            {peserta.lulus === false && <><XCircle className="w-3.5 h-3.5 text-rose-600" /> Tidak Lulus</>}
            {peserta.lulus == null && "-"}
          </p>
        </div>
      </div>
      {peserta.catatan_hasil && <p className="text-xs text-slate-600"><span className="text-slate-400">Catatan: </span>{peserta.catatan_hasil}</p>}
      {sert.nomor && (
        <div className="pt-2 border-t border-slate-200 text-sm">
          <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Award className="w-3.5 h-3.5" /> Sertifikat</p>
          <p className="font-medium">{sert.nomor}</p>
          <p className="text-xs text-slate-500">
            Terbit {sert.tanggal_terbit ? formatTanggal(sert.tanggal_terbit) : "-"}
            {sert.berlaku_sampai && (
              <> · Berlaku s.d. <span className={peserta.sertifikat_kedaluwarsa ? "text-rose-600 font-medium" : ""}>
                {formatTanggal(sert.berlaku_sampai)}{peserta.sertifikat_kedaluwarsa ? " (kedaluwarsa)" : ""}
              </span></>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

function PesertaEditModal({ peserta, onClose, onSave }) {
  const [status, setStatus] = useState(peserta.status || "terdaftar");
  const [nilai, setNilai] = useState(peserta.nilai ?? "");
  const [lulus, setLulus] = useState(peserta.lulus == null ? "" : String(peserta.lulus));
  const [catatan, setCatatan] = useState(peserta.catatan_hasil || "");
  const [sert, setSert] = useState({ ...emptySertifikat, ...(peserta.sertifikat || {}) });

  const submit = () => {
    const patch = {
      status,
      nilai: nilai === "" ? null : Number(nilai),
      lulus: lulus === "" ? null : lulus === "true",
      catatan_hasil: catatan,
      sertifikat: sert,
    };
    onSave(patch);
  };

  // Form penilaian + sertifikat (8 field) — komponen ini di-mount ulang tiap
  // dibuka (parent pakai `{editPesertaFor && <PesertaEditModal ... />}`),
  // jadi `active` bawaan (true) sudah cukup.
  const guard = useModalGuard({ onClose, onSubmit: submit, dangerous: true });

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h3 className="font-display font-semibold text-slate-900 truncate">{peserta.employee_nama}</h3>
          <button onClick={guard.requestClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Status Kehadiran</span>
            <select value={status} onChange={e => setStatus(e.target.value)} data-testid="peserta-status"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              {STATUS_PESERTA.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Nilai (0-100)</span>
              <input type="number" min={0} max={100} value={nilai} onChange={e => setNilai(e.target.value)}
                data-testid="peserta-nilai"
                className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Kelulusan</span>
              <select value={lulus} onChange={e => setLulus(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                <option value="">-</option>
                <option value="true">Lulus</option>
                <option value="false">Tidak Lulus</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Catatan Hasil</span>
            <textarea rows={2} value={catatan} onChange={e => setCatatan(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </label>

          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-1">
              <Award className="w-3.5 h-3.5" /> Sertifikat
            </p>
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Nomor Sertifikat</span>
                <input value={sert.nomor} onChange={e => setSert(s => ({ ...s, nomor: e.target.value }))}
                  data-testid="sertifikat-nomor"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Tanggal Terbit</span>
                  <DatePicker value={sert.tanggal_terbit || ""} onChange={v => setSert(s => ({ ...s, tanggal_terbit: v }))} className="mt-1" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-slate-600">Berlaku Sampai</span>
                  <DatePicker value={sert.berlaku_sampai || ""} onChange={v => setSert(s => ({ ...s, berlaku_sampai: v }))} className="mt-1" />
                </label>
              </div>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Catatan Sertifikat</span>
                <input value={sert.catatan} onChange={e => setSert(s => ({ ...s, catatan: e.target.value }))}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button onClick={guard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
            <button onClick={submit} data-testid="btn-save-peserta"
              className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium">
              Simpan
            </button>
          </div>
          {guard.confirming && (
            <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="peserta-modal-guard-bar" />
          )}
        </div>
      </div>
    </div>
  );
}

// Sengaja disalin dari Projects.jsx (bukan diimpor lintas halaman) — pola
// yang sama persis dipakai di ≥2 tempat sudah jadi jebakan duplikasi helper
// versi lain (lihat CLAUDE.md); ini kecil & UI-only jadi risikonya rendah,
// tapi kalau diskusi ini butuh diubah lagi, cek Projects.jsx juga.
function CommentsThread({ detailFor, userEmail, canDeleteAny, onAdd, onDelete }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    const ok = await onAdd(text);
    setSending(false);
    if (ok) { setText(""); inputRef.current?.focus(); }
  };

  const comments = detailFor.komentar || [];
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare className="w-4 h-4 text-emerald-600" />
        <p className="text-xs text-slate-500 font-medium">Diskusi ({comments.length})</p>
      </div>

      {comments.length === 0 ? (
        <p className="text-xs text-slate-400 py-3 text-center border border-dashed border-slate-200 rounded-lg">
          Belum ada komentar.
        </p>
      ) : (
        <ul className="space-y-2 max-h-60 overflow-y-auto pr-1" data-testid="comments-list">
          {comments.map(c => {
            const isMine = c.author_email === userEmail;
            const canDel = isMine || canDeleteAny;
            const initial = (c.author_name || "?")[0]?.toUpperCase();
            return (
              <li key={c.id} className={`flex gap-2 ${isMine ? "flex-row-reverse" : ""}`} data-testid={`comment-${c.id}`}>
                <div className={`w-8 h-8 rounded-full grid place-items-center text-xs font-semibold shrink-0 ${
                  isMine ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"
                }`}>{initial}</div>
                <div className={`flex-1 min-w-0 ${isMine ? "text-right" : ""}`}>
                  <div className={`inline-block max-w-full text-left px-3 py-2 rounded-2xl text-sm ${
                    isMine ? "bg-emerald-50 border border-emerald-100" : "bg-slate-50 border border-slate-100"
                  }`}>
                    <p className="text-[11px] font-semibold text-slate-700 mb-0.5">
                      {c.author_name}
                      <span className="ml-1.5 text-[10px] text-slate-400 font-normal uppercase">{c.author_role}</span>
                    </p>
                    <p className="whitespace-pre-wrap break-words text-slate-800">{c.text}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{formatTanggal(c.created_at, { denganJam: true })}</p>
                  </div>
                  {canDel && (
                    <button onClick={() => onDelete(c.id)} data-testid={`btn-del-comment-${c.id}`}
                      className="text-[10px] text-slate-400 hover:text-rose-600 mt-0.5 ml-1">
                      hapus
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-end gap-2 mt-3">
        <textarea ref={inputRef} rows={2} value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); submit(); } }}
          placeholder="Tulis komentar... (Ctrl+Enter untuk kirim)" data-testid="comment-input"
          className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-600/40" />
        <button onClick={submit} disabled={!text.trim() || sending} data-testid="btn-send-comment"
          className="h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-1.5">
          <Send className="w-3.5 h-3.5" /> Kirim
        </button>
      </div>
    </div>
  );
}
